import React, { useState, useEffect } from "react";
import {
  Box, Button, CircularProgress, Grid, MenuItem, TextField, Typography,
} from "@mui/material";
import useAdd from "../../../../api/useAdd";
import useUpdate from "../../../../api/useUpdate";
import useFetchObjects from "../../../../api/useFetchObjects";

const empty = {
  expense_date: "", ambulance: "", ambulance_code: "", plate_number: "",
  driver: "", expense_type: "", expense_source: "",
  amount: "", invoice_number: "", description: "", attachment: null,
};

const AmbulanceExpenseForm = ({ expense, onSuccess, token }) => {
  const isEdit = !!expense;
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data: ambulances } = useFetchObjects(["amb-list"], "ambulances/", token);
  const { data: drivers } = useFetchObjects(["drv-list"], "drivers/", token);
  const { data: expenseTypes } = useFetchObjects(["exp-types"], "expense-types/", token);
  const { data: expenseSources } = useFetchObjects(["exp-sources"], "expense-sources/", token);

  const { handleAdd, loading: addLoading, resetForm } = useAdd("ambulance-expenses", token);
  const { handleUpdate, loading: updateLoading } = useUpdate("ambulance-expenses", token, null);
  const loading = addLoading || updateLoading;

  useEffect(() => {
    if (expense) {
      setForm({
        expense_date: expense.expense_date || "",
        ambulance: expense.ambulance || "",
        ambulance_code: expense.ambulance_code || "",
        plate_number: expense.plate_number || "",
        driver: expense.driver || "",
        expense_type: expense.expense_type || "",
        expense_source: expense.expense_source || "",
        amount: expense.amount || "",
        invoice_number: expense.invoice_number || "",
        description: expense.description || "",
        attachment: null,
      });
    } else {
      setForm(empty);
    }
    setErrors({});
  }, [expense]);

  useEffect(() => { if (resetForm) onSuccess(); }, [resetForm]); // eslint-disable-line

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "ambulance") {
      const amb = Array.isArray(ambulances) && ambulances.find((a) => a.id === Number(value));
      setForm((prev) => ({
        ...prev,
        ambulance: value,
        ambulance_code: amb ? amb.ambulance_code : "",
        plate_number: amb ? amb.plate_number : "",
        driver: amb?.assigned_driver || "",
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validate = () => {
    const e = {};
    if (!form.expense_date) e.expense_date = "Date is required";
    if (!form.ambulance) e.ambulance = "Ambulance is required";
    if (!form.amount) e.amount = "Amount is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== null && v !== "") fd.append(k, v);
    });
    if (isEdit) {
      handleUpdate(expense.id, fd);
      setTimeout(() => onSuccess(), 1000);
    } else {
      handleAdd(fd);
    }
  };

  const tf = (label, name, type = "text") => (
    <Grid item xs={12} sm={6}>
      <TextField label={label} name={name} value={form[name]} onChange={handleChange}
        type={type} fullWidth size="small" error={!!errors[name]} helperText={errors[name]}
        InputLabelProps={type === "date" ? { shrink: true } : undefined} />
    </Grid>
  );

  const expTypeList = Array.isArray(expenseTypes) ? expenseTypes : (expenseTypes?.results || []);
  const expSrcList = Array.isArray(expenseSources) ? expenseSources : (expenseSources?.results || []);
  const driverList = Array.isArray(drivers) ? drivers : (drivers?.results || []);
  const ambList = Array.isArray(ambulances) ? ambulances : (ambulances?.results || []);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        {tf("Expense Date *", "expense_date", "date")}

        <Grid item xs={12} sm={6}>
          <TextField select label="Ambulance *" name="ambulance" value={form.ambulance}
            onChange={handleChange} fullWidth size="small" error={!!errors.ambulance} helperText={errors.ambulance}>
            <MenuItem value="">— Select —</MenuItem>
            {ambList.map((a) => (
              <MenuItem key={a.id} value={a.id}>{a.ambulance_code} — {a.plate_number}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {tf("Ambulance Code", "ambulance_code")}
        {tf("Plate Number", "plate_number")}

        <Grid item xs={12} sm={6}>
          <TextField select label="Driver" name="driver" value={form.driver}
            onChange={handleChange} fullWidth size="small">
            <MenuItem value="">— None —</MenuItem>
            {driverList.map((d) => (
              <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField select label="Expense Category" name="expense_type" value={form.expense_type}
            onChange={handleChange} fullWidth size="small">
            <MenuItem value="">— Select —</MenuItem>
            {expTypeList.length === 0
              ? <MenuItem disabled>No expense type found</MenuItem>
              : expTypeList.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)
            }
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField select label="Expense Source" name="expense_source" value={form.expense_source}
            onChange={handleChange} fullWidth size="small">
            <MenuItem value="">— Select —</MenuItem>
            {expSrcList.length === 0
              ? <MenuItem disabled>No expense source found</MenuItem>
              : expSrcList.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)
            }
          </TextField>
        </Grid>

        {tf("Amount *", "amount", "number")}
        {tf("Invoice Number", "invoice_number")}

        <Grid item xs={12}>
          <TextField label="Description" name="description" value={form.description}
            onChange={handleChange} fullWidth size="small" multiline rows={2} />
        </Grid>

        <Grid item xs={12}>
          <Button variant="outlined" component="label" size="small">
            Attachment
            <input type="file" hidden onChange={(e) =>
              setForm((prev) => ({ ...prev, attachment: e.target.files[0] || null }))} />
          </Button>
          {form.attachment && (
            <Typography variant="caption" display="block" mt={0.5}>
              {form.attachment.name}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
        <Button variant="outlined" onClick={onSuccess} disabled={loading}>Cancel</Button>
        <Button type="submit" variant="contained" color="primary" disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}>
          {isEdit ? "Update" : "Add Expense"}
        </Button>
      </Box>
    </Box>
  );
};

export default AmbulanceExpenseForm;
